// ===== BEGIN FILE: src/utils/lifeAreas.ts =====
/**
 * Life Area Definitions for Chat Analysis
 * 
 * Maps life aspects to relevant houses and divisional charts (vargas)
 * for focused astrological analysis in the chat interface.
 */

export type VargaKey = 
  | 'D1' | 'D2' | 'D3' | 'D4' | 'D7' | 'D9' | 'D10' | 'D12'
  | 'D16' | 'D20' | 'D24' | 'D27' | 'D30' | 'D40' | 'D45' | 'D60';

export type LifeAreaKey = 
  | 'self'
  | 'health'
  | 'wealth'
  | 'job'
  | 'business'
  | 'marriage'
  | 'children'
  | 'education'
  | 'property'
  | 'mother'
  | 'father';

export interface LifeAreaDefinition {
  key: LifeAreaKey;
  label: string;
  description: string;
  houses: number[]; // Relevant houses to analyze
  vargas: VargaKey[]; // Relevant divisional charts
  icon?: string; // Optional emoji/icon
  color?: string; // Optional Joy UI color theme
}

/**
 * Life Area Configuration
 * Defines which houses and divisional charts are relevant for each life aspect
 */
export const LIFE_AREAS: Record<LifeAreaKey, LifeAreaDefinition> = {
  self: {
    key: 'self',
    label: 'Self & Personality',
    description: 'Analyze overall personality, character, physical body, and self-identity',
    houses: [1],
    vargas: ['D1'],
    icon: '🌟',
    color: 'primary'
  },
  
  health: {
    key: 'health',
    label: 'Health & Vitality',
    description: 'Analyze physical health, diseases, vitality, and wellness',
    houses: [1, 6, 8, 12],
    vargas: ['D1', 'D27'], // D27 = Saptavimshamsa for strength/weakness
    icon: '🌿',
    color: 'danger'
  },
  
  wealth: {
    key: 'wealth',
    label: 'Wealth & Finance',
    description: 'Analyze income, savings, investments, fortune, and financial gains',
    houses: [1, 2, 5, 9, 11, 12],
    vargas: ['D1', 'D2'], // D2 = Hora for wealth
    icon: '🪙',
    color: 'success'
  },
  
  job: {
    key: 'job',
    label: 'Job & Career',
    description: 'Analyze employment, profession, workplace, and career advancement',
    houses: [1, 6, 10],
    vargas: ['D1', 'D10'], // D10 = Dasamsa for career/profession
    icon: '🧭',
    color: 'warning'
  },
  
  business: {
    key: 'business',
    label: 'Business & Enterprise',
    description: 'Analyze entrepreneurship, business ventures, partnerships, and trade',
    houses: [1, 3, 7, 11],
    vargas: ['D1', 'D10'], // D10 also relevant for business
    icon: '🌐',
    color: 'neutral'
  },
  
  marriage: {
    key: 'marriage',
    label: 'Marriage & Relationships',
    description: 'Analyze spouse, marriage quality, partnerships, and romantic relationships',
    houses: [1, 7],
    vargas: ['D1', 'D9'], // D9 = Navamsa for marriage/spouse
    icon: '💍',
    color: 'danger'
  },
  
  children: {
    key: 'children',
    label: 'Children & Progeny',
    description: 'Analyze children, fertility, creativity, and progeny matters',
    houses: [1, 5],
    vargas: ['D1', 'D7'], // D7 = Saptamsa for children
    icon: '🌱',
    color: 'success'
  },
  
  education: {
    key: 'education',
    label: 'Education & Learning',
    description: 'Analyze academic success, higher education, knowledge acquisition, and learning abilities',
    houses: [2, 4, 5],
    vargas: ['D1', 'D24'], // D24 = Chaturvimshamsa for education/learning
    icon: '📜',
    color: 'warning'
  },
  
  property: {
    key: 'property',
    label: 'Home, Property & Vehicles',
    description: 'Analyze real estate, home, vehicles, comforts, and material assets',
    houses: [1, 4],
    vargas: ['D1', 'D4', 'D16'], // D4 = Chaturthamsa for property, D16 = Shodasamsa for vehicles
    icon: '🗝️',
    color: 'primary'
  },
  
  mother: {
    key: 'mother',
    label: 'Mother',
    description: 'Analyze relationship with mother, maternal influence, and mother\'s wellbeing',
    houses: [4],
    vargas: ['D1'],
    icon: '🌙',
    color: 'warning'
  },
  
  father: {
    key: 'father',
    label: 'Father',
    description: 'Analyze relationship with father, paternal influence, and father\'s wellbeing',
    houses: [9],
    vargas: ['D1'],
    icon: '☀️',
    color: 'neutral'
  }
};

/**
 * Get life area definition by key
 */
export function getLifeArea(key: LifeAreaKey): LifeAreaDefinition {
  return LIFE_AREAS[key];
}

/**
 * Get all life area keys as array
 */
export function getLifeAreaKeys(): LifeAreaKey[] {
  return Object.keys(LIFE_AREAS) as LifeAreaKey[];
}

/**
 * Get suggested questions for a specific life area
 */
export function getLifeAreaQuestions(key: LifeAreaKey): string[] {
  const questions: Record<LifeAreaKey, string[]> = {
    self: [
      "What are my key personality traits?",
      "How do others perceive me?",
      "What are my natural strengths and weaknesses?"
    ],
    health: [
      "What health issues should I be careful about?",
      "Which body parts are most vulnerable in my chart?",
      "When will my health improve?",
      "What remedies can improve my vitality?"
    ],
    wealth: [
      "Will I accumulate significant wealth?",
      "What are my best sources of income?",
      "When will my financial situation improve?",
      "Do I have yogas for wealth and prosperity?"
    ],
    job: [
      "What career is best suited for me?",
      "When will I get a job promotion?",
      "Should I change my current job?",
      "What does my 10th house indicate about my career?"
    ],
    business: [
      "Should I start my own business?",
      "Will my business be successful?",
      "What type of business is good for me?",
      "When is a good time to start a venture?"
    ],
    marriage: [
      "When will I get married?",
      "What kind of spouse will I have?",
      "Will my marriage be happy and harmonious?",
      "What does my 7th house indicate about relationships?"
    ],
    children: [
      "Will I have children?",
      "How many children will I have?",
      "When is the best time for childbirth?",
      "What does my 5th house indicate about progeny?"
    ],
    education: [
      "Will I be successful in higher education?",
      "What field of study is best suited for me?",
      "When is a favorable time for pursuing advanced degrees?",
      "What does my chart indicate about learning abilities?"
    ],
    property: [
      "Will I own property and vehicles?",
      "When is a good time to buy a house?",
      "What does my 4th house indicate about home?",
      "Will I inherit property from family?"
    ],
    mother: [
      "What is my relationship with my mother like?",
      "How is my mother's health and wellbeing?",
      "What does my chart indicate about maternal influence?"
    ],
    father: [
      "What is my relationship with my father like?",
      "How is my father's health and wellbeing?",
      "What does my chart indicate about paternal influence?"
    ]
  };
  
  return questions[key] || [];
}

// ===== LIFE AREA GROUPS (for desktop sidebar tree view) =====

export interface LifeAreaGroup {
  id: string;
  label: string;
  icon: string;
  areas: LifeAreaKey[];
}

export const LIFE_AREA_GROUPS: LifeAreaGroup[] = [
  {
    id: 'personal',
    label: 'Personal',
    icon: '✨',
    areas: ['self', 'health', 'education']
  },
  {
    id: 'financial',
    label: 'Financial & Career',
    icon: '💎',
    areas: ['wealth', 'job', 'business', 'property']
  },
  {
    id: 'relationships',
    label: 'Relationships & Family',
    icon: '🌙',
    areas: ['marriage', 'children', 'mother', 'father']
  }
];

// ===== FOLLOW-UP SUGGESTIONS (client-side, no API cost) =====

/**
 * Returns 3 contextual follow-up question suggestions for a given life area.
 * These are displayed as chips after an AI response — clicking pre-fills the input
 * but does NOT consume a credit until the user actually sends the message.
 */
export function getFollowUpSuggestions(lifeArea: LifeAreaKey | null): string[] {
  const suggestions: Record<string, string[]> = {
    self: [
      "How does my ascendant lord's placement affect my personality?",
      "What is the current dasha doing to my overall confidence?",
      "Which planets are giving me strength vs challenges right now?",
      "How does my Moon nakshatra influence my emotional patterns?",
      "What does my Atmakaraka reveal about my soul's purpose?",
      "How do others perceive me vs how I actually am?",
      "What are the remedies to strengthen my personal presence?",
      "Which period will bring the most personal growth?"
    ],
    health: [
      "Which specific body system should I watch most carefully?",
      "Is my current dasha lord associated with health challenges?",
      "What natural remedies are indicated by my chart for vitality?",
      "How does Saturn's current position affect my stamina?",
      "Will my health improve in the next 6 months?",
      "Which houses are showing stress that could manifest physically?",
      "What food or lifestyle changes does my chart indicate?",
      "Is there a specific period I should be more careful with health?"
    ],
    wealth: [
      "Which house or planet is primarily blocking financial growth?",
      "When is the next favourable period for income increase?",
      "Do I have strong yogas for sudden wealth or windfall gains?",
      "How is my 11th house lord placed for gains?",
      "Should I focus on savings, investments, or active income right now?",
      "What does my D2 chart say about accumulated wealth?",
      "Is there a Dhana Yoga active in my current dasha?",
      "How can I time my financial decisions this year?"
    ],
    job: [
      "What type of boss or work environment suits me best?",
      "Is a promotion likely in the next 12 months?",
      "How is my 10th lord influencing my career right now?",
      "Should I be looking for opportunities actively or waiting?",
      "What does my D10 chart reveal about career trajectory?",
      "Which dasha periods are historically best for career jumps?",
      "Are there signs of a job change coming up?",
      "What hidden strengths in my chart support career success?"
    ],
    business: [
      "Do I have strong yoga for business success in my chart?",
      "Is this a good time to launch or expand my venture?",
      "What type of business partnership works best for my chart?",
      "How is my 7th house (partnerships) influencing current deals?",
      "What does the D10 say about my entrepreneurial potential?",
      "Which planet needs strengthening for business growth?",
      "Is there risk of financial loss in my upcoming business cycle?",
      "What timing should I aim for to start something new?"
    ],
    marriage: [
      "What kind of partner is most compatible with my chart?",
      "Is there a specific period when marriage is most likely?",
      "What does my Navamsa (D9) reveal about my future spouse?",
      "Are there any Dosha or challenges in my 7th house to address?",
      "How is my Venus positioned for romantic relationships?",
      "What remedies can strengthen my marriage prospects?",
      "Is my current dasha supportive for finding a partner?",
      "What does the 7th lord's placement tell about my spouse?"
    ],
    children: [
      "When is the most favourable period for child birth?",
      "Are there any indications of fertility challenges in my chart?",
      "What does my Saptamsa (D7) reveal about children?",
      "How is my Jupiter placed for progeny blessings?",
      "What does the 5th lord's position indicate about children?",
      "Are there retrograde planets affecting the 5th house?",
      "What remedies support conception and healthy pregnancy?",
      "How many children does my chart indicate?"
    ],
    education: [
      "What field of study is most aligned with my chart?",
      "Is this a good period to pursue higher education?",
      "What does my D24 say about academic potential?",
      "Which planets support or hinder my concentration?",
      "Are there strong Saraswati yoga indicators in my chart?",
      "When is the best dasha for competitive exams?",
      "How does my Mercury placement affect learning ability?",
      "Are there signs of success in professional certifications?"
    ],
    property: [
      "When is the best time to buy property according to my chart?",
      "How is my 4th house lord placed for home stability?",
      "Are there indicators of property disputes in my chart?",
      "What does D4 (Chaturthamsa) say about real estate?",
      "Will I own multiple properties in this lifetime?",
      "Is there a period coming up that's good for relocation?",
      "What direction is most auspicious for my home?",
      "Are there any cautions around vehicle purchases this year?"
    ],
    mother: [
      "How can I improve my relationship with my mother?",
      "Is there a challenging period for my mother's health coming?",
      "What does my 4th house reveal about maternal influence?",
      "How does my Moon sign affect my relationship with mother?",
      "Are there any remedies to strengthen maternal bonds?",
      "What positive influence has my mother had on my chart?",
      "Is the current dasha affecting family harmony at home?"
    ],
    father: [
      "How can I strengthen my relationship with my father?",
      "What does my 9th house reveal about paternal blessings?",
      "Is there a challenging period for my father's health?",
      "How does my Sun placement affect the father relationship?",
      "Are there remedies to honour paternal lineage?",
      "What positive karmic connection do I have with my father?",
      "Does my chart show separation or closeness with father?"
    ],
    general: [
      "What is my strongest house and how can I use it?",
      "Which planet is most influential in my chart right now?",
      "When is my next overall positive period?",
      "What classical yogas do I have and are they active?",
      "How is the current Saturn transit affecting my life?",
      "What does my Atmakaraka planet say about my purpose?",
      "Which life area needs the most attention right now?",
      "What are the top 3 remedies for my current chart situation?"
    ]
  };

  const pool = lifeArea ? (suggestions[lifeArea] || suggestions.general) : suggestions.general;
  // Return 3 pseudo-random suggestions (deterministic based on current minute to avoid flicker)
  const seed = Math.floor(Date.now() / 60000) % pool.length;
  const picks: string[] = [];
  for (let i = 0; i < 3; i++) {
    picks.push(pool[(seed + i) % pool.length]);
  }
  return picks;
}

/**
 * Get divisional chart description for user understanding
 */
export function getVargaDescription(varga: VargaKey): string {
  const descriptions: Record<VargaKey, string> = {
    D1: 'Birth Chart (Rasi) - Overall life blueprint',
    D2: 'Hora - Wealth and financial resources',
    D3: 'Drekkana - Siblings and courage',
    D4: 'Chaturthamsa - Property, land, and fixed assets',
    D7: 'Saptamsa - Children and progeny',
    D9: 'Navamsa - Marriage, spouse, and dharma',
    D10: 'Dasamsa - Career, profession, and status',
    D12: 'Dwadasamsa - Parents and ancestral matters',
    D16: 'Shodasamsa - Vehicles and comforts',
    D20: 'Vimsamsa - Spiritual progress',
    D24: 'Chaturvimshamsa - Education and learning',
    D27: 'Saptavimshamsa - Strength and weaknesses',
    D30: 'Trimsamsa - Evils and misfortunes',
    D40: 'Khavedamsa - Auspicious and inauspicious effects',
    D45: 'Akshavedamsa - General indications',
    D60: 'Shastiamsa - Past life and karmic patterns'
  };
  
  return descriptions[varga] || varga;
}

// ===== END FILE: src/utils/lifeAreas.ts =====
